import React, {FC} from "react";
import {useAxios} from "../../utils/hooks";
import useSafeState from "../../utils/safe-state";
import {Trans} from "@lingui/macro";
import {Breadcrumb, Button, Divider, notification, Popconfirm, Tooltip, Upload, UploadProps} from "antd";
import {DeleteTwoTone, DownloadOutlined, PlusOutlined, UploadOutlined} from "@ant-design/icons/lib";
import DtoTable from "../../components/dto-table/dto-table";
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import ItemWithLocationRuleModel from "../../models/locationassignmentrules/item-with-location-rule-model";
import Drawer from "../../components/drawer/drawer";
import LocationAssignmentRuleDrawer from "../../forms/location-assignment-rule-form";
import {EditOutlined} from "@ant-design/icons";
import {openNotification} from "../../utils/openNotification";
import {message} from "antd";


const LocationAssignmentRulesScreen: FC = () => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [trigger, setTrigger] = useSafeState(false);
    const [xlsLoading, setXlsLoading] = useSafeState(false);

    const locationAssignmentRuleDrawer = Drawer({
        title: <Trans>Location Assignment Rule of Relevant Commodity</Trans>,
        children: <LocationAssignmentRuleDrawer/>,
        customClose: () => setTrigger(function(prev) { return !prev; })
    });

    const handleDownloadTemplate = () => {
        if (axiosInstance.current != null) {
            const FileDownload = require('js-file-download');
            axiosInstance.current({
                url: process.env.REACT_APP_API_BASE_URL + '/resource/location-assignment-rules/download-template',
                method: 'GET',
                responseType: 'blob'
            }).then(function(response: any) {
                FileDownload(response.data, 'location_assignment_rules_template.xlsx');
            }).catch(function(error: any) {
                console.log(error);
            });
        }
    };

    const xlsProps: UploadProps = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        customRequest: async function(options) {
            const file = options.file;
            const onSuccess = options.onSuccess;
            const onError = options.onError;
            if (!axiosInstance.current) return;

            setXlsLoading(true);
            const formData = new FormData();
            formData.append('file', file as any);

            try {
                await axiosInstance.current.post('/resource/location-assignment-rules/upload-xls', formData, {
                    headers: {'Content-Type': 'multipart/form-data'},
                });
                openNotification('customSuccess', 'File successfully uploaded');
                if (onSuccess) onSuccess({}, file as any);
            } catch (error) {
                var err = error as any;
                const details = err && err.response && err.response.data && err.response.data.details;
                if (Array.isArray(details) && details.length > 0) {
                    notification.error({
                        message: <b>Error</b>,
                        description: (
                            <ul style={{margin: 0, paddingLeft: 16}}>
                                {details.map(function(d: string, i: number) { return <li key={i}>{d}</li>; })}
                            </ul>
                        ),
                        placement: 'topRight',
                        duration: 20
                    });
                } else {
                    openNotification('error', err);
                }
                if (onError) onError(err);
            } finally {
                setXlsLoading(false);
            }
        },
        onChange: function(info) {
            if (info.file.status === 'done') {
                setTrigger(function(prev) { return !prev; });
            }
        }
    };

    const handleDelete = (record: any) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .delete('/resource/location-assignment-rules/' + encodeURIComponent(record.ruleId))
                .then(function() {
                    message.success('Successfully deleted.');
                    setTrigger(function(prev) { return !prev; });
                })
                .catch(function() {
                    message.error('Error while deleting');
                });
        }
    };

    const action = (record: any) => (
        <>
            {record.hasRule ? (
                <>
                    <Tooltip title={<Trans>Edit</Trans>}>
                        <EditOutlined className={"ActionButtons"} style={{color: "#40a9ff"}}
                                      onClick={() =>
                                          locationAssignmentRuleDrawer.open("Edit", {
                                              id: record.ruleId,
                                              location: record.location,
                                              item: record.item,
                                              quantity: record.quantity,
                                              validFrom: record.validFrom,
                                              validTo: record.validTo,
                                              version: record.ruleVersion
                                          })
                                      }
                        />
                    </Tooltip>
                    <Tooltip title={<Trans>Remove</Trans>}>
                        <Popconfirm
                            title={<Trans>Are you sure want to delete record?</Trans>}
                            onConfirm={() => handleDelete(record)}
                            okText={<Trans>Yes</Trans>}
                            cancelText={<Trans>No</Trans>}>
                            <DeleteTwoTone className={"ActionButtons"} twoToneColor={"red"}/>
                        </Popconfirm>
                    </Tooltip>
                </>
            ) : (
                <Tooltip title={<Trans>Add rule</Trans>}>
                    <PlusOutlined className={"ActionButtons"} style={{color: "#52c41a"}}
                                  onClick={() =>
                                      locationAssignmentRuleDrawer.open("Add new", {
                                          item: {id: record.itemId}
                                      })
                                  }
                    />
                </Tooltip>
            )}
        </>
    );

    const actions = {
        width: 100,
        render: (text: string, record: any) => action(record),
        title: '',
        fixed: 'right',
        align: 'center'
    };

    const rowClassName = (record: any) => record.hasRule ? 'row-has-rule' : 'row-no-rule';

    const header = (
        <>
            <Button type={'primary'} onClick={() => locationAssignmentRuleDrawer.open('Add new')}>
                <ButtonWithIcon icon={<PlusOutlined/>} text={<Trans>Add new</Trans>}/>
            </Button>
            <Divider type="vertical" style={{fontSize: 'xxx-large'}}/>
            <Button onClick={handleDownloadTemplate}>
                <ButtonWithIcon icon={<DownloadOutlined/>} text={<Trans>.xlsx template</Trans>}/>
            </Button>
            <Divider type="vertical" style={{fontSize: 'xxx-large'}}/>
            <Upload {...xlsProps}>
                <Button loading={xlsLoading}>
                    <ButtonWithIcon icon={<UploadOutlined/>} text={<Trans>.xlsx upload</Trans>}/>
                </Button>
            </Upload>
            <Divider type="vertical" style={{fontSize: 'xxx-large'}}/>
        </>
    );

    return (
        <>
            <Breadcrumb style={{marginBottom: 10}}>
                <Breadcrumb.Item><Trans id={'Home'}/></Breadcrumb.Item>
                <Breadcrumb.Item><Trans>Location Assignment Rules of Relevant Commodity</Trans></Breadcrumb.Item>
            </Breadcrumb>
            <DtoTable
                tableHeader={header}
                model={ItemWithLocationRuleModel}
                action={actions}
                trigger={trigger}
                rowClassName={rowClassName}
                allowRefreshButton={true}
                pageSize={100}
                scroll={{x: 1000, y: "65vh"}}/>
            {locationAssignmentRuleDrawer.component}
        </>
    );
};

export default LocationAssignmentRulesScreen;
